import { createContext, useCallback, useContext, useRef, useState } from 'react';
import FeedbackRenderer from './FeedbackRenderer';

type FeedbackContextType = {
  feedback: string | null;
  callFeedback: (message: string) => void;
  clearFeedback: () => void;
};

const FeedbackContext = createContext<FeedbackContextType | null>(null);

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [feedback, setFeedback] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFeedback = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setFeedback(null);
  }, []);

  const callFeedback = useCallback((message: string) => {
    setFeedback(message);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setFeedback(null);
      timerRef.current = null;
    }, 3000);
  }, []);

  return (
    <FeedbackContext.Provider
      value={{
        feedback,
        callFeedback,
        clearFeedback,
      }}
    >
      {children}
      <FeedbackRenderer feedback={feedback} />
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return ctx;
}